import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import Modal from '../../components/Modal';
import { Heading4Text } from '../../components/Texts';
import TextInput from '../../components/Inputs/Text';
import SearchIcon from '../../assets/icons/search.svg?react';
import { hapticsImpactLight } from '../../common/haptic';
import TokenList from '../../components/TokenList';
import { useTokenSearch } from '../../hooks/useTokenSearch';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0;
`;

const CategoriesContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const CategoryContainerInner = styled.div`
  display: flex;
  padding-top: 5px;
  gap: 16px;
  width: fit-content;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const CategoryPill = styled.button`
  padding: 12px 16px;
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  color: ${({ theme }) => theme.text.regular};
  border: 1px solid ${({ theme }) => theme.border.primary};
  background-color: ${({ theme }) => theme.background.primary};
  font-size: 14px;
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  ${({ $selected, theme }) =>
    $selected &&
    `
    border: 1px solid transparent;
    color: ${theme.primary.primary};
    background-color: ${theme.primary.background};
  `};
  @media ${PHONE_MEDIA_QUERY} {
    padding: 8px;
    font-size: 12px;
  }
`;

const CATEGORIES = ['ALL', 'POPULAR', 'STOCKS', 'PRESTOCKS', 'COMMODITIES'];

const CATEGORY_I18N_KEYS = {
  ALL: 'home.categoryAll',
  POPULAR: 'home.categoryPopular',
  STOCKS: 'home.categoryStocks',
  PRESTOCKS: 'home.categoryPrestocks',
  COMMODITIES: 'home.categoryCommodities',
};

const CATEGORY_ICON = {
  ALL: <img src='/burguer.webp' width={16} height={16} alt='' />,
  POPULAR: '🚀',
  STOCKS: '📈',
  PRESTOCKS: '🔒',
  COMMODITIES: '🌍',
};

export default function BuyTokenSelectModal({ onClose, onContinue, title }) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();

  const {
    searchTerm,
    selectedCategory,
    displayTokens,
    isLoading,
    isFetching,
    hasMore,
    setSearchTerm,
    setSelectedCategory,
    loadMore,
  } = useTokenSearch(null, null);

  const handleTokenSelect = (token) => {
    hapticsImpactLight();
    onClose();
    onContinue?.(token);
  };

  const displayTitle = title ?? t('selectTokenModal.selectToken');

  return (
    <Modal
      onClose={onClose}
      customStyle={
        isMobile && {
          maxHeight: '95vh',
          height: '95vh',
          width: '100vw',
          borderRadius: '10px 10px 0px 0px',
        }
      }
      customOverlayStyle={isMobile ? { alignItems: 'flex-end' } : {}}
    >
      <Container>
        <Heading4Text
          style={{
            fontWeight: '600',
            marginTop: 8,
            color: theme.text.title,
          }}
        >
          {displayTitle}
        </Heading4Text>
        <div style={{ textAlign: 'left' }}>
          <TextInput
            placeholder={t('selectTokenModal.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            endIcon={
              <SearchIcon
                width={20}
                height={20}
                style={{ color: theme.text.regular }}
              />
            }
          />
        </div>
        <CategoriesContainer>
          <CategoryContainerInner>
            {CATEGORIES.map((category) => (
              <CategoryPill
                key={category}
                type='button'
                onClick={() => {
                  hapticsImpactLight();
                  setSelectedCategory(category);
                }}
                $selected={category === selectedCategory}
              >
                {CATEGORY_ICON[category]} {t(CATEGORY_I18N_KEYS[category])}
              </CategoryPill>
            ))}
          </CategoryContainerInner>
        </CategoriesContainer>
        <TokenList
          tokens={displayTokens}
          isLoading={isLoading}
          isFetching={isFetching}
          hasMore={hasMore}
          loadMore={loadMore}
          onTokenSelect={handleTokenSelect}
          showBalancesUSD={false}
          groupByCategory={false}
        />
      </Container>
    </Modal>
  );
}
